import * as THREE from 'three';

/**
 * CameraController - Orthographic camera for unified 3D view
 *
 * Matches Java's camera setup from main.java:341:
 * renderer.ortho(-(width / 2), (width / 2), -(height / 2), (height / 2), -10000, 10000);
 *
 * Provides pan, zoom, and rotate controls for navigating the 3D scene.
 */
export class CameraController {
    /**
     * @param {number} width - Canvas width in pixels
     * @param {number} height - Canvas height in pixels
     */
    constructor(width, height) {
        this.width = width;
        this.height = height;

        // Orthographic camera setup matching Java
        const aspect = width / height;
        const frustumSize = height;

        this.camera = new THREE.OrthographicCamera(
            frustumSize * aspect / -2,  // left
            frustumSize * aspect / 2,   // right
            frustumSize / 2,            // top
            frustumSize / -2,           // bottom
            -10000,                     // near (matches Java)
            10000                       // far (matches Java)
        );

        // Initial camera position (looking down Z axis)
        this.camera.position.set(0, 0, 1000);
        this.camera.lookAt(0, 0, 0);

        // Camera state
        // Java uses ZOOM = 1.5f but with different coordinate scale
        // Adjusted to zoom in 4x more than before for better detail view
        this.zoom = 120.0;  // 4x more zoom (was 30.0)
        this.panOffset = this._defaultPanOffset();
        this.rotation = new THREE.Euler(0, 0, 0);

        // Interaction state
        this.isDragging = false;
        this.lastMousePos = { x: 0, y: 0 };
        this.dragMode = 'pan'; // 'pan', 'rotate', 'zoom'

        // Zoom constraints (adjusted for Three.js coordinate scale)
        this.minZoom = 5.0;  // Minimum zoom (most zoomed out)
        this.maxZoom = 200.0; // Maximum zoom (most zoomed in) - increased for 4x zoom

        // Pan speed
        this.panSpeed = 1.0;

        // Rotation speed
        this.rotateSpeed = 0.005;

        // Apply initial zoom
        this.updateCameraTransform();
    }

    /**
     * Update camera on window resize
     * @param {number} width - New canvas width
     * @param {number} height - New canvas height
     */
    resize(width, height) {
        this.width = width;
        this.height = height;

        // Rebuild the frustum via the shared transform so the current zoom is
        // preserved - setting frustumSize = height here (without / zoom)
        // silently reset the view to fully zoomed out on every resize.
        this.updateCameraTransform();
    }

    /**
     * Pan camera by screen space delta
     * @param {number} deltaX - Screen space X movement
     * @param {number} deltaY - Screen space Y movement
     */
    pan(deltaX, deltaY) {
        // GRAB-THE-WORLD convention: the content follows the cursor
        // (drag right -> chair moves right on screen), so the CAMERA moves
        // the opposite way. Screen Y grows downward while world Y grows
        // upward, so dragging down (deltaY > 0) must RAISE the camera
        // (content moves down). Both axes were previously inverted.
        const worldDeltaX = -deltaX * this.panSpeed / this.zoom;
        const worldDeltaY = deltaY * this.panSpeed / this.zoom;

        this.panOffset.x += worldDeltaX;
        this.panOffset.y += worldDeltaY;

        this.updateCameraTransform();
    }

    /**
     * Zoom camera by delta
     * @param {number} delta - Zoom delta (positive = zoom in, negative = zoom out)
     * @param {number} [centerX] - Optional zoom center X (screen space)
     * @param {number} [centerY] - Optional zoom center Y (screen space)
     */
    zoomBy(delta, centerX, centerY) {
        const zoomFactor = 1 + delta * 0.1;
        const newZoom = this.zoom * zoomFactor;

        // Clamp zoom
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));

        this.updateCameraTransform();
    }

    /**
     * Rotate camera/scene
     * @param {number} deltaX - Rotation around Y axis
     * @param {number} deltaY - Rotation around X axis
     */
    rotate(deltaX, deltaY) {
        this.rotation.y += deltaX * this.rotateSpeed;
        this.rotation.x += deltaY * this.rotateSpeed;

        // Clamp X rotation to prevent flipping
        this.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.rotation.x));

        this.updateCameraTransform();
    }

    /**
     * Apply current transform to camera
     */
    updateCameraTransform() {
        // Apply zoom by scaling frustum
        const aspect = this.width / this.height;
        const frustumSize = this.height / this.zoom;

        this.camera.left = frustumSize * aspect / -2;
        this.camera.right = frustumSize * aspect / 2;
        this.camera.top = frustumSize / 2;
        this.camera.bottom = frustumSize / -2;

        this.camera.updateProjectionMatrix();

        // Apply pan
        this.camera.position.x = this.panOffset.x;
        this.camera.position.y = this.panOffset.y;
        this.camera.position.z = 1000; // Fixed Z distance

        // Apply rotation
        this.camera.rotation.copy(this.rotation);
    }

    /**
     * Start dragging interaction
     * @param {number} x - Mouse X position
     * @param {number} y - Mouse Y position
     * @param {string} mode - Drag mode: 'pan', 'rotate', 'zoom'
     */
    startDrag(x, y, mode = 'pan') {
        this.isDragging = true;
        this.lastMousePos = { x, y };
        this.dragMode = mode;
    }

    /**
     * Update dragging interaction
     * @param {number} x - Mouse X position
     * @param {number} y - Mouse Y position
     */
    updateDrag(x, y) {
        if (!this.isDragging) return;

        const deltaX = x - this.lastMousePos.x;
        const deltaY = y - this.lastMousePos.y;

        switch (this.dragMode) {
            case 'pan':
                this.pan(deltaX, deltaY);
                break;
            case 'rotate':
                this.rotate(deltaX, deltaY);
                break;
            case 'zoom':
                this.zoomBy(deltaY * 0.01);
                break;
        }

        this.lastMousePos = { x, y };
    }

    /**
     * End dragging interaction
     */
    endDrag() {
        this.isDragging = false;
    }

    /**
     * Default pan: frame the scene's floor line near the BOTTOM of the
     * viewport (~88% down, just above the bottom dock) instead of
     * mid-screen. The old fixed (0, -4) pan left the horizon at ~63%
     * height, wasting the lower third of the screen on under-floor void -
     * chairs live ABOVE the ground, so that's where the editing space
     * should be. Computed from zoom + window height so any screen size
     * gets the same framing.
     */
    _defaultPanOffset() {
        const GROUND_Y = -5;              // scene floor (Application groundY)
        const GROUND_SCREEN_FRACTION = 0.88; // floor line, fraction from top
        const visibleHeight = this.height / this.zoom;
        return new THREE.Vector3(
            0, GROUND_Y + visibleHeight * (GROUND_SCREEN_FRACTION - 0.5), 0);
    }

    /**
     * Frame an axis-aligned box so it fills most of the viewport.
     * @param {THREE.Box3} box - bounds to frame
     * @param {number} fillFraction - how much of the viewport the box
     *   should occupy (0..1) along its tighter dimension
     * @param {THREE.Matrix4|null} worldMatrix - transform from the box's
     *   frame into world (pass the scene-rotation pivot's matrixWorld for
     *   pivot-local bounds like the figure/physics - the view rotation
     *   lives on that pivot GROUP, not on this camera)
     */
    zoomToFit(box, fillFraction = 0.75, worldMatrix = null) {
        if (!box || box.isEmpty()) return;

        // Box corners in WORLD space
        const corners = [];
        for (const x of [box.min.x, box.max.x])
            for (const y of [box.min.y, box.max.y])
                for (const z of [box.min.z, box.max.z]) {
                    const c = new THREE.Vector3(x, y, z);
                    if (worldMatrix) c.applyMatrix4(worldMatrix);
                    corners.push(c);
                }

        // Extents along the camera's screen axes (camera.rotation is
        // normally identity - scene rotation happens on the pivot group)
        const right = new THREE.Vector3(1, 0, 0).applyEuler(this.rotation);
        const up = new THREE.Vector3(0, 1, 0).applyEuler(this.rotation);
        let minR = Infinity, maxR = -Infinity, minU = Infinity, maxU = -Infinity;
        for (const c of corners) {
            const r = c.dot(right);
            const u = c.dot(up);
            minR = Math.min(minR, r); maxR = Math.max(maxR, r);
            minU = Math.min(minU, u); maxU = Math.max(maxU, u);
        }
        const extentR = Math.max(maxR - minR, 1e-6);
        const extentU = Math.max(maxU - minU, 1e-6);

        // zoom = screen px per world unit
        const zoom = Math.min(
            this.width * fillFraction / extentR,
            this.height * fillFraction / extentU);
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));

        // Centre the box: choose panOffset (camera x/y) so the screen-axis
        // coordinates of the camera position match the box centre. With no
        // camera rotation this is just the centre's x/y; solve the 2x2
        // system for the general case (camera z stays fixed at 1000).
        const cR = (minR + maxR) / 2;
        const cU = (minU + maxU) / 2;
        const det = right.x * up.y - right.y * up.x;
        if (Math.abs(det) > 1e-6) {
            const bR = cR - 1000 * right.z;
            const bU = cU - 1000 * up.z;
            this.panOffset.x = (bR * up.y - bU * right.y) / det;
            this.panOffset.y = (bU * right.x - bR * up.x) / det;
        } else {
            const center = new THREE.Vector3();
            for (const c of corners) center.add(c);
            center.divideScalar(corners.length);
            this.panOffset.x = center.x;
            this.panOffset.y = center.y;
        }

        this.updateCameraTransform();
    }

    /**
     * Reset camera to default view
     */
    reset() {
        this.zoom = 120.0;  // Reset to default zoom (4x more than original)
        this.panOffset.copy(this._defaultPanOffset());
        this.rotation.set(0, 0, 0);
        this.updateCameraTransform();
    }

    /**
     * Get camera instance for Three.js renderer
     * @returns {THREE.OrthographicCamera}
     */
    getCamera() {
        return this.camera;
    }

    /**
     * Set drag mode for mouse interactions
     * @param {string} mode - 'pan', 'rotate', or 'zoom'
     */
    setDragMode(mode) {
        this.dragMode = mode;
    }

    /**
     * Convert screen coordinates to world coordinates at Z=0
     * @param {number} screenX - Screen X position
     * @param {number} screenY - Screen Y position
     * @returns {THREE.Vector3} World position
     */
    screenToWorld(screenX, screenY) {
        // Convert to NDC (-1 to 1)
        const ndcX = (screenX / this.width) * 2 - 1;
        const ndcY = -(screenY / this.height) * 2 + 1;

        // Unproject from camera
        const vector = new THREE.Vector3(ndcX, ndcY, 0);
        vector.unproject(this.camera);

        return vector;
    }

    /**
     * Convert world coordinates to screen coordinates
     * @param {THREE.Vector3} worldPos - World position
     * @returns {{x: number, y: number}} Screen position
     */
    worldToScreen(worldPos) {
        const vector = worldPos.clone();
        vector.project(this.camera);

        const screenX = (vector.x + 1) * this.width / 2;
        const screenY = -(vector.y - 1) * this.height / 2;

        return { x: screenX, y: screenY };
    }
}
